'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Button } from './button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';

export interface DirtyEntry {
  /** Shown in the leave dialog, e.g. the tab name "Details". */
  label: string;
  /** Saves; resolve true on success. A false keeps the user where they are (the surface shows its own error). */
  onSave?: () => Promise<boolean>;
  /** Restores the clean state before navigation proceeds on Discard. */
  onDiscard?: () => void;
}

/**
 * `proceed` may be async (sign out, then a hard redirect): until it settles the browser's unload
 * prompt stays quiet, because the user has already chosen to leave. A proceed that hard-navigates
 * must keep its promise pending after assigning `location.href` (`await new Promise<never>(() =>
 * {})`): WebKit fires beforeunload after the assignment returns, so a promise that settles there
 * re-arms the prompt first. A proceed that fails should settle, so the guard re-arms.
 */
type Guard = (
  proceed: () => void | Promise<unknown>,
  options?: { only?: string },
) => Promise<boolean>;

interface DirtyGuardContextValue {
  register: (id: string, entry: DirtyEntry) => void;
  unregister: (id: string) => void;
  anyDirty: () => boolean;
  guard: Guard;
}

const DirtyGuardContext = createContext<DirtyGuardContextValue | null>(null);

export interface DirtyEntrySummary {
  id: string;
  label: string;
}

const NO_DIRTY_ENTRIES: DirtyEntrySummary[] = [];

// Read-only snapshot of the registry (id + label) for indicators such as a tab's unsaved dot.
// Separate from the guard context: nothing here can register, unregister or guard.
const DirtyEntriesContext = createContext<DirtyEntrySummary[]>(NO_DIRTY_ENTRIES);

// Bumped each time a leave empties registry entries. Kept apart from the main context so only
// registering surfaces re-render: a surface that stayed mounted and dirty registers itself again.
const RegistryGenerationContext = createContext(0);

const LEAVE_ACTION = 'w-full whitespace-nowrap sm:w-auto';

interface Pending {
  ids: string[];
  entries: DirtyEntry[];
  proceed: () => void | Promise<unknown>;
  resolve: (left: boolean) => void;
}

/**
 * One registry of surfaces with unsaved changes. Mounted once in AppShell. Anything that leaves
 * a surface (tab strip, Cancel, any same-origin link, page unload) goes through `guard()`.
 * Browser Back/Forward is not interceptable in the App Router and is not covered.
 */
export function DirtyGuardProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('Common.unsavedChanges');
  const router = useRouter();
  const entries = useRef(new Map<string, DirtyEntry>());
  const [dirtyCount, setDirtyCount] = useState(0);
  const [generation, setGeneration] = useState(0);
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  // Async proceeds of confirmed leaves still running (see Guard).
  const leavingRef = useRef(0);

  const [summary, setSummary] = useState<DirtyEntrySummary[]>(NO_DIRTY_ENTRIES);
  const publish = useCallback(() => {
    const next = [...entries.current].map(([id, entry]) => ({ id, label: entry.label }));
    setSummary((prev) =>
      prev.length === next.length &&
      prev.every((p, i) => p.id === next[i].id && p.label === next[i].label)
        ? prev
        : next.length === 0
          ? NO_DIRTY_ENTRIES
          : next,
    );
  }, []);

  const register = useCallback(
    (id: string, entry: DirtyEntry) => {
      entries.current.set(id, entry);
      setDirtyCount(entries.current.size);
      publish();
    },
    [publish],
  );

  const unregister = useCallback(
    (id: string) => {
      if (entries.current.delete(id)) {
        setDirtyCount(entries.current.size);
        publish();
      }
    },
    [publish],
  );

  const anyDirty = useCallback(() => entries.current.size > 0, []);

  // Resolves exactly the attempt it is given; the dialog closes only if that attempt is still the
  // open one, so a stale settle can never resolve (or close) a newer attempt.
  const settle = useCallback((current: Pending, left: boolean) => {
    if (pendingRef.current === current) {
      pendingRef.current = null;
      setPending(null);
      savingRef.current = false;
      setSaving(false);
    }
    current.resolve(left);
  }, []);

  const stay = useCallback(() => {
    const current = pendingRef.current;
    if (current) settle(current, false);
  }, [settle]);

  const guard = useCallback<Guard>((proceed, options) => {
    // A save is already deciding where the user goes; any other leave attempt is refused.
    if (savingRef.current) return Promise.resolve(false);
    const list = [...entries.current].filter(([id]) => !options?.only || id === options.only);
    if (list.length === 0) {
      proceed();
      return Promise.resolve(true);
    }
    // A second leave attempt while the dialog is open replaces the first.
    pendingRef.current?.resolve(false);
    return new Promise<boolean>((resolve) => {
      const next: Pending = {
        ids: list.map(([id]) => id),
        entries: list.map(([, entry]) => entry),
        proceed,
        resolve,
      };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const leave = useCallback(
    (current: Pending) => {
      for (const id of current.ids) entries.current.delete(id);
      setDirtyCount(entries.current.size);
      publish();
      // Any surface still mounted and dirty after this (no onDiscard, or the proceed stayed on the
      // page) registers again, so the guard never silently turns off.
      setGeneration((g) => g + 1);
      settle(current, true);
      const result = current.proceed();
      if (result instanceof Promise) {
        leavingRef.current += 1;
        void result.finally(() => {
          leavingRef.current -= 1;
        });
      }
    },
    [settle, publish],
  );

  const discard = useCallback(() => {
    const current = pendingRef.current;
    if (!current) return;
    for (const entry of current.entries) entry.onDiscard?.();
    leave(current);
  }, [leave]);

  const saveAndContinue = useCallback(async () => {
    const current = pendingRef.current;
    if (!current || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    for (const entry of current.entries) {
      // A save that throws (sync or async) is a failed save: the user stays put, nothing stays busy.
      let ok: boolean;
      try {
        ok = entry.onSave ? await entry.onSave() : false;
      } catch {
        ok = false;
      }
      if (!ok) {
        settle(current, false);
        return;
      }
    }
    leave(current);
  }, [leave, settle]);

  // Reload / tab close while dirty: the browser shows its own prompt.
  useEffect(() => {
    if (dirtyCount === 0) return;
    function onBeforeUnload(event: BeforeUnloadEvent) {
      // leave() empties the registry before it proceeds; this listener lives until the next commit.
      // A surface still dirty after the leave registers again, so an async proceed (sign out, then
      // redirect) is covered by leavingRef instead, for as long as that proceed stays pending.
      if (entries.current.size === 0 || leavingRef.current > 0) return;
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirtyCount]);

  // Any same-origin link (sidebar, breadcrumbs, Cancel links) while dirty asks first. Runs in the
  // capture phase on document, before next/link's handler, which skips prevented events.
  useEffect(() => {
    if (dirtyCount === 0) return;
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      if (anchor.closest('[data-dirty-guard-ignore]')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // The exact current URL (with or without a hash) does not leave the surface.
      if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return;
      }
      event.preventDefault();
      void guard(() => router.push(url.pathname + url.search + url.hash));
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirtyCount, guard, router]);

  const value = useMemo(
    () => ({ register, unregister, anyDirty, guard }),
    [register, unregister, anyDirty, guard],
  );

  const canSave = pending?.entries.every((entry) => entry.onSave) ?? false;
  const labels = pending?.entries.map((entry) => entry.label).join(', ') ?? '';

  return (
    <DirtyGuardContext.Provider value={value}>
      <RegistryGenerationContext.Provider value={generation}>
        <DirtyEntriesContext.Provider value={summary}>{children}</DirtyEntriesContext.Provider>
      </RegistryGenerationContext.Provider>
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) stay();
        }}
        busy={saving}
        size="md"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>
          <p className="text-muted px-6 py-4 text-sm">{t('body', { labels })}</p>
          {/* Three actions: stacked full width on phones, one line each and wrapping from sm up. */}
          <DialogFooter className="sm:flex-wrap">
            <Button
              variant="ghost"
              onClick={stay}
              disabled={saving}
              data-autofocus=""
              className={LEAVE_ACTION}
            >
              {t('keepEditing')}
            </Button>
            <Button variant="outline" onClick={discard} disabled={saving} className={LEAVE_ACTION}>
              {t('discard')}
            </Button>
            {canSave && (
              <Button
                onClick={() => void saveAndContinue()}
                loading={saving}
                className={LEAVE_ACTION}
              >
                {t('saveAndContinue')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DirtyGuardContext.Provider>
  );
}

/** Registers this surface as having unsaved changes while `dirty` is true. No-op outside the provider. */
export function useDirtyGuard(id: string, dirty: boolean, entry: DirtyEntry): void {
  const ctx = useContext(DirtyGuardContext);
  const generation = useContext(RegistryGenerationContext);

  useEffect(() => {
    if (!ctx) return;
    if (dirty) ctx.register(id, entry);
    else ctx.unregister(id);
  }, [ctx, id, dirty, entry, generation]);

  useEffect(() => {
    if (!ctx) return;
    return () => ctx.unregister(id);
  }, [ctx, id]);
}

/** The surfaces currently holding unsaved changes (read-only). Empty outside the provider. */
export function useDirtyEntries(): DirtyEntrySummary[] {
  return useContext(DirtyEntriesContext);
}

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const passThrough: Guard = (proceed) => {
  proceed();
  return Promise.resolve(true);
};

/** The leave guard for programmatic navigation and close buttons. Outside the provider it just proceeds. */
export function useLeaveGuard(): { guard: Guard; anyDirty: () => boolean } {
  const ctx = useContext(DirtyGuardContext);
  return useMemo(
    () =>
      ctx
        ? { guard: ctx.guard, anyDirty: ctx.anyDirty }
        : { guard: passThrough, anyDirty: () => false },
    [ctx],
  );
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

// Leaf values by dotted path. Arrays, empty objects and non-plain objects are leaves, compared by JSON.
function flattenLeaves(value: unknown, prefix: string, out: Map<string, string>): void {
  if (isPlainObject(value) && Object.keys(value).length > 0) {
    for (const [key, child] of Object.entries(value)) {
      flattenLeaves(child, prefix ? `${prefix}.${key}` : key, out);
    }
    return;
  }
  // JSON drops undefined keys, so an undefined leaf is the same as an absent one.
  if (value === undefined) return;
  out.set(prefix, String(JSON.stringify(value)));
}

/** The dotted paths of leaf values that differ between two snapshots, sorted. */
export function changedPaths(baseline: unknown, current: unknown): string[] {
  const before = new Map<string, string>();
  const after = new Map<string, string>();
  flattenLeaves(baseline, '', before);
  flattenLeaves(current, '', after);
  const paths = new Set([...before.keys(), ...after.keys()]);
  return [...paths].filter((path) => before.get(path) !== after.get(path)).sort();
}

/**
 * Turns changed paths into the distinct field labels shown on the bar. `labelFor` returns the
 * label of a known path (or undefined); every unknown path collapses into the one `otherLabel`,
 * listed last. Labels keep the order of the sorted paths and are de-duplicated.
 */
export function summariseChanges(
  paths: string[],
  labelFor: (path: string) => string | undefined,
  otherLabel: string,
): string[] {
  const labels: string[] = [];
  let hasOther = false;
  for (const path of paths) {
    const label = labelFor(path);
    if (label === undefined) hasOther = true;
    else if (!labels.includes(label)) labels.push(label);
  }
  if (hasOther) labels.push(otherLabel);
  return labels;
}

/**
 * Dirty = the user has interacted with the container AND the value differs from what it was just
 * before that first interaction. Load-time auto-fill (effects that set fields) never counts.
 * Values are compared with JSON.stringify: track File objects by name, not by reference.
 *
 * After an async save, call `markSaved(sent)` with the snapshot the request actually sent, not
 * `reset()`: fields stay editable while the request is in flight, and anything typed meanwhile
 * must stay dirty. `reset()` is for Discard (and for a surface that closes once it has saved).
 */
export function useInteractionDirty<T>(value: T): {
  dirty: boolean;
  baseline: T | null;
  /** Dotted paths that differ from the baseline; empty while clean. */
  changedPaths: string[];
  reset: () => void;
  markSaved: (saved: T) => void;
  containerProps: { onPointerDownCapture: () => void; onKeyDownCapture: () => void };
} {
  const serialized = JSON.stringify(value);
  const latest = useRef({ serialized, value });
  const touchedRef = useRef(false);
  const [baseline, setBaseline] = useState<{ serialized: string; value: T } | null>(null);

  // Layout effect, so a keypress right after an auto-fill commit already sees the filled value.
  useIsomorphicLayoutEffect(() => {
    latest.current = { serialized, value };
  });

  const markTouched = useCallback(() => {
    if (touchedRef.current) return;
    touchedRef.current = true;
    setBaseline(latest.current);
  }, []);

  const reset = useCallback(() => {
    touchedRef.current = false;
    setBaseline(null);
  }, []);

  // The saved snapshot becomes the clean state. If nothing was typed since it was sent, this is a
  // plain reset, so a post-save refetch that fills fields still counts as auto-fill, not an edit.
  const markSaved = useCallback(
    (saved: T) => {
      const savedSerialized = JSON.stringify(saved);
      if (savedSerialized === latest.current.serialized) {
        reset();
        return;
      }
      touchedRef.current = true;
      setBaseline({ serialized: savedSerialized, value: saved });
    },
    [reset],
  );

  const containerProps = useMemo(
    () => ({ onPointerDownCapture: markTouched, onKeyDownCapture: markTouched }),
    [markTouched],
  );

  const dirty = baseline !== null && serialized !== baseline.serialized;
  const baselineSerialized = baseline?.serialized ?? null;
  const paths = useMemo(
    () => (dirty && baseline ? changedPaths(baseline.value, value) : []),
    // Keyed on the serialized values: the snapshots themselves change identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirty, serialized, baselineSerialized],
  );

  return {
    dirty,
    baseline: baseline?.value ?? null,
    changedPaths: paths,
    reset,
    markSaved,
    containerProps,
  };
}

/**
 * One save request at a time, for a surface whose Save can also be called by the leave dialog's
 * Save and continue. `run` sends the current values; `value` is what the surface tracks (the same
 * value given to `useInteractionDirty`). A call while a request is in flight waits for it; then,
 * with `resaveIfChanged`, if the tracked value moved since that request was sent, it saves again
 * with the latest values and resolves with that result. Without it (a create, which must never
 * POST twice) the call just shares the pending result.
 */
export function useSingleFlightSave<T>(
  run: () => Promise<boolean>,
  value: T,
  resaveIfChanged: boolean,
): () => Promise<boolean> {
  const serialized = JSON.stringify(value);
  const latest = useRef({ run, serialized, resaveIfChanged });
  useIsomorphicLayoutEffect(() => {
    latest.current = { run, serialized, resaveIfChanged };
  });
  const inFlight = useRef<{ request: Promise<boolean>; sent: string } | null>(null);

  return useMemo(() => {
    const save = (): Promise<boolean> => {
      const pending = inFlight.current;
      if (pending) {
        if (!latest.current.resaveIfChanged) return pending.request;
        // `request` settles after its own cleanup, so a resave here starts a fresh request.
        return pending.request.then((ok) =>
          ok && latest.current.serialized !== pending.sent ? save() : ok,
        );
      }
      const sent = latest.current.serialized;
      const request = latest.current.run().finally(() => {
        if (inFlight.current?.request === request) inFlight.current = null;
      });
      inFlight.current = { request, sent };
      return request;
    };
    return save;
  }, []);
}

const BAR_LABEL_LIMIT = 3;

/** Sticky bottom bar for long forms; render it as the last child inside the form. */
export function UnsavedChangesBar({
  visible,
  saving = false,
  onSave,
  onDiscard,
  changes,
}: {
  visible: boolean;
  saving?: boolean;
  onSave?: () => void;
  onDiscard: () => void;
  /** Distinct, already translated field labels that changed; names up to three and counts the rest. */
  changes?: string[];
}) {
  const t = useTranslations('Common.unsavedChanges');
  const tCommon = useTranslations('Common');
  if (!visible) return null;
  return (
    <div
      role="region"
      aria-label={t('barMessage')}
      className="bg-card border-border sticky bottom-0 z-20 mt-6 flex items-center justify-between gap-3 border-t px-4 pt-3"
      // viewportFit: 'cover' lets the page run under iOS Safari's floating toolbar; keep the bar above it.
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <span className="text-muted text-sm">
        {changes && changes.length > 0
          ? `${t('barCount', {
              count: changes.length,
              labels: changes.slice(0, BAR_LABEL_LIMIT).join(t('barSeparator')),
            })}${
              changes.length > BAR_LABEL_LIMIT
                ? ` ${t('barMore', { n: changes.length - BAR_LABEL_LIMIT })}`
                : ''
            }`
          : t('barMessage')}
      </span>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" onClick={onDiscard} disabled={saving}>
          {t('discard')}
        </Button>
        {onSave && (
          <Button type="button" onClick={onSave} loading={saving}>
            {tCommon('buttons.save')}
          </Button>
        )}
      </div>
    </div>
  );
}
